import React from 'react';

import LandingNav from '../landing/components/LandingNav';
import LandingFooter from '../landing/components/LandingFooter';

const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="flex-shrink-0" style={{ color: '#059669' }}>
    <path d="M20 6L9 17l-5-5"/>
  </svg>
);

const regulatoryFrameworks = [
  {
    badge: 'AIFMD',
    title: 'Alternative Investment Fund Managers Directive',
    description: 'SeedDrive\'s workflows are designed to support AIFMD compliance obligations including investor disclosure, risk management, and reporting requirements for alternative investment fund managers operating in the EU.',
    items: ['Investor disclosure templates', 'Risk management frameworks', 'Regulatory reporting support', 'Depositary integration pathway'],
  },
  {
    badge: 'FCA',
    title: 'Financial Conduct Authority',
    description: 'Platform controls and audit trails are structured to support FCA-regulated firms, with configurable permissions, client categorisation, and suitability assessment workflows aligned to UK regulatory requirements.',
    items: ['Client categorisation controls', 'Suitability assessment tools', 'Audit trail and record-keeping', 'Regulatory permissions management'],
  },
  {
    badge: 'GDPR',
    title: 'General Data Protection Regulation',
    description: 'Data handling practices are built around GDPR principles including data minimisation, purpose limitation, and subject access rights. All personal data is processed with appropriate legal basis and documented consent.',
    items: ['Data minimisation by design', 'Subject access request support', 'Consent management', 'Data processing agreements'],
  },
];

const securityPractices = [
  'AES-256 encryption at rest for all stored data',
  'TLS 1.3 encryption in transit for all communications',
  'Role-based access controls with least-privilege principles',
  'Multi-factor authentication for all user accounts',
  'Continuous security monitoring and anomaly detection',
  'Regular penetration testing by independent third parties',
  'SOC 2 Type II audit in progress',
  'Automated vulnerability scanning and patch management',
];

const kycAmlItems = [
  'Automated identity verification via accredited providers',
  'Sanctions screening against OFAC, EU, and UN lists',
  'PEP (Politically Exposed Person) screening',
  'Adverse media monitoring and ongoing monitoring',
  'Risk-based due diligence workflows (standard, enhanced)',
  'Jurisdiction-specific AML policy templates',
  'Audit-ready KYC documentation storage',
  'Compliance officer review and approval workflows',
];

const complianceSummary = [
  { area: 'Data encryption', status: 'Active', detail: 'AES-256 at rest, TLS 1.3 in transit' },
  { area: 'Access controls', status: 'Active', detail: 'RBAC with MFA enforcement' },
  { area: 'KYC/AML screening', status: 'Active', detail: 'Automated + manual review workflows' },
  { area: 'GDPR compliance', status: 'Active', detail: 'Data minimisation and consent management' },
  { area: 'AIFMD alignment', status: 'In progress', detail: 'Disclosure and reporting templates' },
  { area: 'SOC 2 Type II', status: 'In progress', detail: 'Audit underway with Big 4 firm' },
  { area: 'RWA tokenisation', status: 'Roadmap', detail: 'Regulatory sandbox engagement' },
];

export default function SecurityCompliancePage() {
  return (
    <div className="min-h-screen" style={{ background: '#F2F0EC' }}>
      <LandingNav />
      {/* Hero */}
      <section className="pt-32 pb-16 px-6 lg:px-8" style={{ background: '#F2F0EC' }}>
        <div className="max-w-7xl mx-auto">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-caption font-medium mb-6 border" style={{ borderColor: 'rgba(59,76,76,0.15)', color: 'var(--color-secondary)', background: 'rgba(59,76,76,0.04)' }}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: '#3b4c4c' }}></span>
              Security & compliance
            </div>
            <h1 className="font-heading text-4xl lg:text-5xl font-semibold leading-tight mb-5" style={{ color: 'var(--color-primary)' }}>Institutional-grade security. Regulatory-ready by design.</h1>
            <p className="text-lg text-gray-600 font-body leading-relaxed">SeedDrive is built from the ground up for regulated markets. Every control, workflow, and data practice is designed to meet the standards institutional investors and regulators expect.</p>
          </div>
        </div>
      </section>
      {/* Trust badges */}
      <section className="">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-center gap-6">
            {['AIFMD Aligned', 'FCA Compliant', 'GDPR Compliant', 'AES-256 Encrypted', 'SOC 2 In Progress', 'TLS 1.3']?.map((badge) => (
              <div key={badge} className="flex items-center gap-2 px-4 py-2 rounded-full border" style={{ borderColor: 'rgba(5,150,105,0.3)', background: 'rgba(5,150,105,0.04)' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ color: '#059669' }}>
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                </svg>
                <span className="text-xs font-caption font-medium" style={{ color: '#059669' }}>{badge}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Regulatory frameworks */}
      <section className="">
        <div className="max-w-7xl mx-auto">
          <div className="mb-12">
            <h2 className="font-heading text-3xl font-semibold mb-3" style={{ color: 'var(--color-primary)' }}>Regulatory framework</h2>
            <p className="text-gray-600 font-body max-w-xl">SeedDrive\'s platform architecture is aligned to the key regulatory frameworks governing institutional alternative investment activity.</p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {regulatoryFrameworks?.map((fw) => (
              <div key={fw?.badge} className="p-7 rounded-xl border" style={{ borderColor: 'var(--color-border)' }}>
                <div className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-data font-semibold mb-4" style={{ background: 'var(--color-primary)', color: 'white' }}>
                  {fw?.badge}
                </div>
                <h3 className="font-heading text-lg font-semibold mb-2" style={{ color: 'var(--color-primary)' }}>{fw?.title}</h3>
                <p className="text-sm text-gray-600 font-body leading-relaxed mb-5">{fw?.description}</p>
                <ul className="space-y-2">
                  {fw?.items?.map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <CheckIcon />
                      <span className="text-sm font-body text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Data security */}
      <section className="py-20 px-6 lg:px-8" style={{ background: '#ECEAE4' }}>
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="font-heading text-3xl font-semibold mb-3" style={{ color: 'var(--color-primary)' }}>Data security practices</h2>
              <p className="text-gray-600 font-body leading-relaxed mb-8">Security is not an afterthought at SeedDrive. Our infrastructure is designed with defence-in-depth principles, ensuring your data and your investors\' data is protected at every layer.</p>
              <ul className="space-y-3">
                {securityPractices?.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <CheckIcon />
                    <span className="text-sm font-body text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h2 className="font-heading text-3xl font-semibold mb-3" style={{ color: 'var(--color-primary)' }}>KYC/AML framework</h2>
              <p className="text-gray-600 font-body leading-relaxed mb-8">Every investor onboarded through SeedDrive passes through a rigorous, risk-based KYC/AML process designed to meet the standards of regulated financial markets.</p>
              <ul className="space-y-3">
                {kycAmlItems?.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <CheckIcon />
                    <span className="text-sm font-body text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>
      {/* RWA tokenisation compliance */}
      <section className="">
        <div className="max-w-4xl mx-auto">
          <div className="p-8 rounded-xl border" style={{ borderColor: 'rgba(59,76,76,0.4)', background: 'rgba(59,76,76,0.04)' }}>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(59,76,76,0.15)', color: 'var(--color-primary)' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M13 10V3L4 14h7v7l9-11h-7z"/>
                </svg>
              </div>
              <div>
                <h3 className="font-heading text-xl font-semibold mb-2" style={{ color: 'var(--color-primary)' }}>RWA Tokenisation Compliance Note</h3>
                <p className="text-gray-600 font-body leading-relaxed mb-4">SeedDrive\'s RWA tokenisation pathway is designed for regulated environments. We are actively engaged with financial regulators in key jurisdictions to ensure our tokenisation framework meets applicable securities law, AML obligations, and investor protection requirements.</p>
                <p className="text-gray-600 font-body leading-relaxed">Tokenisation features are available under regulatory sandbox arrangements and to eligible professional investors only. All tokenised instruments are backed by legally enforceable off-chain documentation and subject to the same KYC/AML controls as traditional instruments.</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Compliance summary table */}
      <section className="py-20 px-6 lg:px-8" style={{ background: '#ECEAE4' }}>
        <div className="max-w-4xl mx-auto">
          <h2 className="font-heading text-3xl font-semibold mb-10" style={{ color: 'var(--color-primary)' }}>Compliance summary</h2>
          <div className="rounded-xl border overflow-hidden shadow-sm" style={{ borderColor: 'var(--color-border)' }}>
            <table className="w-full">
              <thead>
                <tr style={{ background: 'var(--color-primary)' }}>
                  <th className="text-left px-6 py-4 text-sm font-caption font-medium text-white">Area</th>
                  <th className="text-left px-6 py-4 text-sm font-caption font-medium text-white">Status</th>
                  <th className="text-left px-6 py-4 text-sm font-caption font-medium text-white">Detail</th>
                </tr>
              </thead>
              <tbody>
                {complianceSummary?.map((row, i) => (
                  <tr key={row?.area} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-6 py-3.5 text-sm font-body font-medium text-gray-800">{row?.area}</td>
                    <td className="px-6 py-3.5">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-caption font-medium ${
                          row?.status === 'Active' ? 'bg-emerald-50 text-emerald-700' :
                          row?.status === 'In progress'? 'bg-amber-50 text-amber-700' : 'bg-[rgba(59,76,76,0.07)] text-blue-700'
                        }`}
                      >
                        {row?.status}
                      </span>
                    </td>
                    <td className="px-6 py-3.5 text-sm font-body text-gray-600">{row?.detail}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>
      <LandingFooter />
    </div>
  );
}
